import { Chip, Divider, Tooltip, Typography } from "@mui/material";
import { Box, Stack, useTheme } from "@mui/system";
import { useMemo } from "react";
import dayjs from "dayjs";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import useStyles from "./styles";

const formatDuration = (durationMs) => {
  const minutos = Math.max(1, Math.round(durationMs / 60000));
  if (minutos < 60) return `${minutos}min`;
  const horas = Math.floor(minutos / 60);
  const minutosRestantes = minutos % 60;
  return minutosRestantes > 0 ? `${horas}h${minutosRestantes}min` : `${horas}h`;
};

export default function MetricaApiComponent(params) {
  const {
    apiName = "",
    tempo = null,
    index = null,
    resumoDiario = [],
    dias = [],
    analiseAtividadeApiDay = () => {},
    analiseAtividadeApi = () => {},
    windowWidth = null,
    apiConfigs = [],
  } = params;

  const theme = useTheme();
  const styles = useStyles(theme);

  // Mapa de acesso rápido: data (YYYY-MM-DD) -> resumo do dia (só existe para dias com checagens)
  const resumoPorData = useMemo(() => {
    const map = new Map();
    resumoDiario?.forEach((resumo) => map.set(resumo.date, resumo));
    return map;
  }, [resumoDiario]);

  const diasVisiveis = useMemo(() => dias?.slice(-tempo) || [], [dias, tempo]);

  const resumosVisiveis = useMemo(
    () =>
      diasVisiveis.map((dia) => resumoPorData.get(dia.data)).filter(Boolean),
    [diasVisiveis, resumoPorData],
  );

  const corPorPercentual = (percentual) =>
    percentual > 90
      ? theme.palette.colors.green.light
      : percentual > 80
        ? theme.palette.colors.yellow.dark
        : theme.palette.colors.red.dark;

  return (
    <Box sx={styles.containerApi}>
      <Box sx={styles.containerTituloApi}>
        <Box sx={styles.containerColorApi} bgcolor={apiConfigs[index]?.color} />

        <Typography sx={styles.apiTitulo}>
          {apiName?.toLowerCase() === "ixc" ? apiName.toUpperCase() : apiName}
        </Typography>
      </Box>

      <Box sx={styles.containerAnaliseApi}>
        {diasVisiveis.map((dia) => {
          const resumo = resumoPorData.get(dia.data);
          const percentual = resumo ? analiseAtividadeApiDay(resumo) : 0;

          const temIncidentes = resumo?.incidents?.length > 0;

          return (
            <Tooltip
              key={dia.data}
              arrow
              PopperProps={{
                modifiers: [
                  {
                    name: "preventOverflow",
                    options: {
                      boundary: "viewport",
                    },
                  },
                ],
              }}
              componentsProps={styles.tooltipStyles}
              title={
                resumo ? (
                  <Box sx={{ minWidth: "220px" }}>
                    <Box sx={styles.containerTooltipHeader}>
                      <Typography sx={styles.tituloData}>
                        {dayjs(resumo.date).format("DD/MM/YYYY")}
                      </Typography>
                      <Chip
                        size="small"
                        icon={
                          temIncidentes ? (
                            <ErrorOutlineIcon />
                          ) : (
                            <CheckCircleOutlineIcon />
                          )
                        }
                        label={
                          temIncidentes
                            ? `${resumo.incidents.length} incidente${
                                resumo.incidents.length > 1 ? "s" : ""
                              }`
                            : "Operacional"
                        }
                        sx={{
                          ...styles.statusChip,
                          bgcolor: temIncidentes
                            ? theme.palette.colors.red.dark
                            : theme.palette.colors.green.light,
                        }}
                      />
                    </Box>

                    {temIncidentes ? (
                      <Box sx={styles.containerIncidentes}>
                        {resumo.incidents.map((incidente, indexIncidente) => (
                          <Box key={incidente.start.toString()}>
                            {indexIncidente > 0 && <Divider />}
                            <Box sx={styles.containerIncidenteItem}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                gap="6px"
                              >
                                <Box sx={styles.containerStatusFalha} />
                                <Typography sx={styles.tituloRequisicao}>
                                  {dayjs(incidente.start).format("HH:mm")}
                                  {incidente.end !== incidente.start &&
                                    ` – ${dayjs(incidente.end).format("HH:mm")}`}
                                </Typography>
                              </Stack>
                              <Typography sx={styles.tituloDuracao}>
                                {formatDuration(incidente.durationMs)}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Stack direction="row" alignItems="center" gap="6px">
                        <Box sx={styles.containerStatusRequisicao} />
                        <Typography sx={styles.tituloRequisicao}>
                          Nenhuma falha registrada
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                ) : (
                  <>
                    <Box sx={styles.containerSemDados}>
                      <Typography sx={styles.tituloData}>
                        {dayjs(dia?.data)?.format("DD/MM/YYYY")}
                      </Typography>
                    </Box>

                    <Typography sx={styles.tituloRequisicao}>
                      Sem dados para esse período
                    </Typography>
                  </>
                )
              }
            >
              <Box
                bgcolor={
                  resumo
                    ? corPorPercentual(percentual)
                    : theme.palette.mode === "dark"
                      ? theme.palette.colors.black.medium
                      : theme.palette.colors.black.light
                }
                sx={{
                  ...styles.containerDia,
                  width:
                    windowWidth > 2500
                      ? "8px"
                      : windowWidth < 1829
                        ? "7px"
                        : "5px",
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
      <Box sx={styles.containerAnaliseDias}>
        <Box sx={{ minWidth: "60px" }}>
          <Typography sx={styles.tituloDias}>Há {tempo} dias</Typography>
        </Box>
        <Box sx={styles.containerDivider}>
          <Divider variant="fullWidth">
            <Chip
              label={`${analiseAtividadeApi(resumosVisiveis)}% em atividade`}
              size="small"
              sx={{
                bgcolor: corPorPercentual(analiseAtividadeApi(resumosVisiveis)),
                color: theme.palette.white.main,
                fontWeight: "bold",
              }}
            />
          </Divider>
        </Box>
        <Box sx={styles.containerHoje}>
          <Typography sx={styles.tituloDias}>Hoje</Typography>
        </Box>
      </Box>
    </Box>
  );
}
